import { useEffect, useMemo, useRef, useState } from "react";
import "./perfectPitch.css";

const PITCHES = [
  {
    id: "c",
    note: "C",
    colorName: "Red",
    color: "#ef4444",
    rgb: "239, 68, 68",
    ink: "#fff7f7",
    frequency: 261.63,
    isBlackKey: false,
  },
  {
    id: "c-sharp",
    note: "C#",
    colorName: "Vermilion",
    color: "#f97316",
    rgb: "249, 115, 22",
    ink: "#211007",
    frequency: 277.18,
    isBlackKey: true,
  },
  {
    id: "d",
    note: "D",
    colorName: "Amber",
    color: "#f59e0b",
    rgb: "245, 158, 11",
    ink: "#211700",
    frequency: 293.66,
    isBlackKey: false,
  },
  {
    id: "d-sharp",
    note: "D#",
    colorName: "Gold",
    color: "#eab308",
    rgb: "234, 179, 8",
    ink: "#1f1a05",
    frequency: 311.13,
    isBlackKey: true,
  },
  {
    id: "e",
    note: "E",
    colorName: "Lime",
    color: "#84cc16",
    rgb: "132, 204, 22",
    ink: "#101a06",
    frequency: 329.63,
    isBlackKey: false,
  },
  {
    id: "f",
    note: "F",
    colorName: "Green",
    color: "#22c55e",
    rgb: "34, 197, 94",
    ink: "#06160b",
    frequency: 349.23,
    isBlackKey: false,
  },
  {
    id: "f-sharp",
    note: "F#",
    colorName: "Teal",
    color: "#14b8a6",
    rgb: "20, 184, 166",
    ink: "#041615",
    frequency: 369.99,
    isBlackKey: true,
  },
  {
    id: "g",
    note: "G",
    colorName: "Sky",
    color: "#0ea5e9",
    rgb: "14, 165, 233",
    ink: "#03131d",
    frequency: 392,
    isBlackKey: false,
  },
  {
    id: "g-sharp",
    note: "G#",
    colorName: "Blue",
    color: "#2563eb",
    rgb: "37, 99, 235",
    ink: "#f6f8ff",
    frequency: 415.3,
    isBlackKey: true,
  },
  {
    id: "a",
    note: "A",
    colorName: "Indigo",
    color: "#4f46e5",
    rgb: "79, 70, 229",
    ink: "#f8f7ff",
    frequency: 440,
    isBlackKey: false,
  },
  {
    id: "a-sharp",
    note: "A#",
    colorName: "Violet",
    color: "#a855f7",
    rgb: "168, 85, 247",
    ink: "#fff8ff",
    frequency: 466.16,
    isBlackKey: true,
  },
  {
    id: "b",
    note: "B",
    colorName: "Rose",
    color: "#f43f5e",
    rgb: "244, 63, 94",
    ink: "#fff7f8",
    frequency: 493.88,
    isBlackKey: false,
  },
];

const LEVELS = [
  {
    id: "triad",
    label: "Triad",
    pitchIds: ["c", "e", "g"],
  },
  {
    id: "white",
    label: "White keys",
    pitchIds: ["c", "d", "e", "f", "g", "a", "b"],
  },
  {
    id: "chromatic",
    label: "Chromatic",
    pitchIds: PITCHES.map((pitch) => pitch.id),
  },
];

const OCTAVE_RANGES = [
  {
    id: "one",
    label: "1 octave",
    octaves: [4],
  },
  {
    id: "two",
    label: "2 octaves",
    octaves: [3, 4],
  },
  {
    id: "three",
    label: "3 octaves",
    octaves: [3, 4, 5],
  },
];

const DEFAULT_LEVEL_ID = "white";
const DEFAULT_OCTAVE_RANGE_ID = "one";
const AUTO_ADVANCE_DELAY = 3000;
const PITCH_BY_ID = Object.fromEntries(PITCHES.map((pitch) => [pitch.id, pitch]));
const INITIAL_STATS = {
  round: 1,
  attempts: 0,
  correct: 0,
  streak: 0,
  bestStreak: 0,
  history: [],
};

function getLevelPitches(levelId) {
  const level = LEVELS.find((candidate) => candidate.id === levelId) || LEVELS[0];
  return level.pitchIds.map((pitchId) => PITCH_BY_ID[pitchId]);
}

function getOctaves(rangeId) {
  const range =
    OCTAVE_RANGES.find((candidate) => candidate.id === rangeId) ||
    OCTAVE_RANGES[0];
  return range.octaves;
}

function getTargetFrequency(pitch, octave) {
  return pitch.frequency * 2 ** (octave - 4);
}

function chooseTarget(pitches, octaves, previousTarget = null) {
  const targets = pitches.flatMap((pitch) =>
    octaves.map((octave) => ({
      pitchId: pitch.id,
      octave,
    }))
  );
  const options =
    targets.length > 1
      ? targets.filter(
          (target) =>
            target.pitchId !== previousTarget?.pitchId ||
            target.octave !== previousTarget?.octave
        )
      : targets;
  const index = Math.floor(Math.random() * options.length);
  return options[index];
}

function getAccuracy({ attempts, correct }) {
  if (attempts === 0) {
    return "0%";
  }

  return `${Math.round((correct / attempts) * 100)}%`;
}

function getAudioContext(audioContextRef) {
  if (audioContextRef.current) {
    return audioContextRef.current;
  }

  const AudioContextConstructor = window.AudioContext || window.webkitAudioContext;

  if (!AudioContextConstructor) {
    return null;
  }

  audioContextRef.current = new AudioContextConstructor();
  return audioContextRef.current;
}

function playPitch(frequency, audioContextRef) {
  const audioContext = getAudioContext(audioContextRef);

  if (!audioContext) {
    return;
  }

  if (audioContext.state === "suspended") {
    audioContext.resume();
  }

  const startAt = audioContext.currentTime + 0.01;
  const stopAt = startAt + 1.28;
  const masterGain = audioContext.createGain();
  const filter = audioContext.createBiquadFilter();
  const partials = [
    { ratio: 1, gain: 0.82, type: "sine" },
    { ratio: 2, gain: 0.18, type: "triangle" },
    { ratio: 3, gain: 0.08, type: "sine" },
  ];

  filter.type = "lowpass";
  filter.frequency.setValueAtTime(2800, startAt);
  masterGain.gain.setValueAtTime(0.0001, startAt);
  masterGain.gain.exponentialRampToValueAtTime(0.28, startAt + 0.025);
  masterGain.gain.exponentialRampToValueAtTime(0.09, startAt + 0.42);
  masterGain.gain.exponentialRampToValueAtTime(0.0001, stopAt);
  filter.connect(masterGain);
  masterGain.connect(audioContext.destination);

  partials.forEach((partial) => {
    const oscillator = audioContext.createOscillator();
    const partialGain = audioContext.createGain();

    oscillator.type = partial.type;
    oscillator.frequency.setValueAtTime(frequency * partial.ratio, startAt);
    partialGain.gain.setValueAtTime(partial.gain, startAt);
    oscillator.connect(partialGain);
    partialGain.connect(filter);
    oscillator.start(startAt);
    oscillator.stop(stopAt + 0.02);
  });
}

function PerfectPitchPage() {
  const audioContextRef = useRef(null);
  const [levelId, setLevelId] = useState(DEFAULT_LEVEL_ID);
  const [octaveRangeId, setOctaveRangeId] = useState(DEFAULT_OCTAVE_RANGE_ID);
  const activePitches = useMemo(() => getLevelPitches(levelId), [levelId]);
  const activeOctaves = useMemo(() => getOctaves(octaveRangeId), [octaveRangeId]);
  const keyboardKeys = useMemo(
    () =>
      activeOctaves.flatMap((octave) =>
        PITCHES.map((pitch) => ({
          pitch,
          octave,
        }))
      ),
    [activeOctaves]
  );
  const [target, setTarget] = useState(() =>
    chooseTarget(
      getLevelPitches(DEFAULT_LEVEL_ID),
      getOctaves(DEFAULT_OCTAVE_RANGE_ID)
    )
  );
  const [stats, setStats] = useState(INITIAL_STATS);
  const [feedback, setFeedback] = useState(null);
  const targetPitch = PITCH_BY_ID[target.pitchId] || activePitches[0];
  const targetFrequency = getTargetFrequency(targetPitch, target.octave);
  const activeLevel = LEVELS.find((level) => level.id === levelId);
  const activeOctaveRange = OCTAVE_RANGES.find(
    (range) => range.id === octaveRangeId
  );

  useEffect(() => {
    if (!feedback) {
      return undefined;
    }

    const timerId = window.setTimeout(() => {
      setTarget((currentTarget) =>
        chooseTarget(activePitches, activeOctaves, currentTarget)
      );
      setFeedback(null);
      setStats((currentStats) => ({
        ...currentStats,
        round: currentStats.round + 1,
      }));
    }, AUTO_ADVANCE_DELAY);

    return () => window.clearTimeout(timerId);
  }, [activeOctaves, activePitches, feedback]);

  function handleLevelChange(nextLevelId) {
    const nextPitches = getLevelPitches(nextLevelId);

    setLevelId(nextLevelId);
    setTarget(chooseTarget(nextPitches, activeOctaves));
    setStats(INITIAL_STATS);
    setFeedback(null);
  }

  function handleOctaveRangeChange(nextRangeId) {
    const nextOctaves = getOctaves(nextRangeId);

    setOctaveRangeId(nextRangeId);
    setTarget(chooseTarget(activePitches, nextOctaves));
    setStats(INITIAL_STATS);
    setFeedback(null);
  }

  function handleGuess(pitch) {
    if (feedback) {
      return;
    }

    const isCorrect = pitch.id === target.pitchId;

    setFeedback({
      pitchId: pitch.id,
      isCorrect,
    });
    setStats((currentStats) => {
      const streak = isCorrect ? currentStats.streak + 1 : 0;
      return {
        ...currentStats,
        attempts: currentStats.attempts + 1,
        correct: currentStats.correct + (isCorrect ? 1 : 0),
        streak,
        bestStreak: Math.max(currentStats.bestStreak, streak),
        history: [
          {
            id: `${currentStats.round}-${pitch.id}`,
            pitchId: pitch.id,
            targetId: target.pitchId,
            octave: target.octave,
            isCorrect,
          },
          ...currentStats.history,
        ].slice(0, 12),
      };
    });
  }

  return (
    <main className="perfectPitchPage">
      <div className="perfectPitchShell">
        <header className="perfectPitchTopbar">
          <div className="perfectPitchTitle">
            <p>Ear trainer</p>
            <h1>Pitch Color</h1>
          </div>
          <div className="perfectPitchStats" aria-label="Current score">
            <span>
              <strong>{stats.correct}</strong>
              Hits
            </span>
            <span>
              <strong>{getAccuracy(stats)}</strong>
              Accuracy
            </span>
            <span>
              <strong>{stats.bestStreak}</strong>
              Streak
            </span>
          </div>
        </header>

        <section className="perfectPitchLayout" aria-label="Perfect pitch game">
          <section className="perfectPitchStage" aria-live="polite">
            <button
              className={`perfectPitchTarget ${
                feedback ? "isAnswered" : ""
              } ${feedback?.isCorrect ? "isCorrect" : ""} ${
                feedback && !feedback.isCorrect ? "isMiss" : ""
              }`}
              type="button"
              title="Play pitch"
              aria-label={`Play ${activeLevel.label} pitch`}
              onClick={() => playPitch(targetFrequency, audioContextRef)}
              style={{
                "--target-color": feedback ? targetPitch.color : "#111827",
                "--target-rgb": feedback ? targetPitch.rgb : "17, 24, 39",
                "--target-ink": feedback ? targetPitch.ink : "#f8fafc",
              }}
            >
              <div className="pitchMeter" aria-hidden="true">
                <span />
                <span />
                <span />
                <span />
                <span />
              </div>
              <div className="pitchAnswer">
                <span>
                  {feedback
                    ? targetPitch.colorName
                    : `${activeLevel.label} - ${activeOctaveRange.label}`}
                </span>
                <strong>
                  {feedback ? `${targetPitch.note}${target.octave}` : "?"}
                </strong>
              </div>
            </button>

            <div
              className="pitchKeyboard"
              aria-hidden="true"
              style={{
                gridTemplateColumns: `repeat(${keyboardKeys.length}, minmax(0, 1fr))`,
              }}
            >
              {keyboardKeys.map(({ pitch, octave }) => (
                <span
                  key={`${pitch.id}-${octave}`}
                  className={`pitchKey ${pitch.isBlackKey ? "isBlackKey" : ""} ${
                    activePitches.some((activePitch) => activePitch.id === pitch.id)
                      ? "isInLevel"
                      : ""
                  } ${
                    feedback &&
                    pitch.id === target.pitchId &&
                    octave === target.octave
                      ? "isTarget"
                      : ""
                  }`}
                  style={{
                    "--key-color": pitch.color,
                    "--key-rgb": pitch.rgb,
                  }}
                />
              ))}
            </div>
          </section>

          <section className="perfectPitchBoard" aria-label="Pitch color choices">
            <div className="perfectPitchLevelTabs" aria-label="Level">
              {LEVELS.map((level) => (
                <button
                  key={level.id}
                  className={level.id === levelId ? "isSelected" : ""}
                  type="button"
                  aria-pressed={level.id === levelId}
                  onClick={() => handleLevelChange(level.id)}
                >
                  {level.label}
                </button>
              ))}
            </div>

            <div className="perfectPitchOctaveTabs" aria-label="Octave range">
              {OCTAVE_RANGES.map((range) => (
                <button
                  key={range.id}
                  className={range.id === octaveRangeId ? "isSelected" : ""}
                  type="button"
                  aria-pressed={range.id === octaveRangeId}
                  onClick={() => handleOctaveRangeChange(range.id)}
                >
                  {range.label}
                </button>
              ))}
            </div>

            <div className="pitchColorGrid">
              {activePitches.map((pitch) => {
                const isSelected = feedback?.pitchId === pitch.id;
                const isAnswer = feedback && targetPitch.id === pitch.id;
                const isMiss = isSelected && !feedback.isCorrect;

                return (
                  <button
                    key={pitch.id}
                    className={`pitchColorTile ${isSelected ? "isSelected" : ""} ${
                      isAnswer ? "isAnswer" : ""
                    } ${isMiss ? "isMiss" : ""}`}
                    type="button"
                    aria-label={`${pitch.note} ${pitch.colorName}`}
                    disabled={Boolean(feedback)}
                    onClick={() => handleGuess(pitch)}
                    style={{
                      "--pitch-color": pitch.color,
                      "--pitch-rgb": pitch.rgb,
                      "--pitch-ink": pitch.ink,
                    }}
                  >
                    <span>{pitch.colorName}</span>
                    <strong>{pitch.note}</strong>
                  </button>
                );
              })}
            </div>

            <div className="pitchHistory" aria-label="Recent answers">
              {stats.history.length === 0 ? (
                <span className="pitchHistoryEmpty" />
              ) : (
                stats.history.map((entry) => {
                  const pitch = PITCH_BY_ID[entry.targetId];

                  return (
                    <span
                      key={entry.id}
                      className={entry.isCorrect ? "isCorrect" : "isMiss"}
                      title={`${pitch.note}${entry.octave} ${pitch.colorName}`}
                      style={{
                        "--history-color": pitch.color,
                        "--history-rgb": pitch.rgb,
                      }}
                    />
                  );
                })
              )}
            </div>
          </section>
        </section>
      </div>
    </main>
  );
}

export default PerfectPitchPage;
